import { cn } from '@rocco/utils/cn';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import {
    BLEED_BREAKOUT_X,
    BLEED_GUTTER_X,
    TileCarousel,
} from '~/ui-rocco/pages/detail-shared';
import {
    brandToTile,
    PROFESSIONAL_TILE_LOGO,
    PROFESSIONAL_TILE_MEDIA,
    PROFESSIONAL_TILE_SIZES,
    PROFESSIONAL_TILE_TEXT,
} from './professional-tile-props';

/**
 * The brand family as a headerless carousel — the overview accordion's
 * "Explore our Brand Family" row. A carousel rather than the Network tab's grid
 * because a large family (six brands for the Altus group) would otherwise run
 * the accordion two rows deep inside a collapsed section; the accordion row
 * supplies the numbered title. Renders nothing without a family.
 */
export const ProfessionalBrandFamilyCarousel = () => {
    const family = useProfileFamily();
    if (!family) return null;

    return (
        // Full bleed: the wrapper cancels the accordion's gutter so the slides
        // scroll to the viewport edges, and the viewport puts the same distance
        // back as padding so the first snap still lines up with the row title.
        //
        // This looks like it double-counts the breakout the accordion row already
        // applies to widen Radix's clip, and it doesn't: BLEED_GUTTER_X is
        // `100%`-relative, so on an element the `100vw` breakout has already
        // widened by exactly that much, the two cancel. Measured at 1920 / 2400 /
        // 2560 / 3200: clip spans the viewport, the first tile stays on the row
        // title, tiles hold 438px with 24px gaps and the fourth ends on the cap.
        <div
            className={cn(
                'pb-[1.8rem] max-md:-mx-5 max-md:pb-4',
                BLEED_BREAKOUT_X,
            )}
        >
            <TileCarousel
                eyebrow=""
                hideHeader
                tiles={family.brands.map(brandToTile)}
                viewportClassName={cn('max-md:px-5', BLEED_GUTTER_X)}
                // Four across inside the gutter, matching the Network tab's grid
                // so the same brand reads at the same size on both surfaces. Two
                // things to get right: the basis nets off the three 24px gaps
                // (otherwise the fourth tile overflows), and `md:px-0` drops the
                // carousel's own per-item half-gap, which would otherwise stack
                // on the gap below and shave 11px off every tile.
                itemClassName="max-md:basis-[46%] md:basis-[calc((100%_-_4.5rem)/4)] md:px-0"
                contentClassName="max-md:gap-2 md:gap-[1.5rem]"
                tileMediaClassName={PROFESSIONAL_TILE_MEDIA}
                tileLogoClassName={PROFESSIONAL_TILE_LOGO}
                tileTextClassName={PROFESSIONAL_TILE_TEXT}
                tileSizes={PROFESSIONAL_TILE_SIZES}
            />
        </div>
    );
};
